import { Action, createHook, createStore } from "react-sweet-state";

import {
  authenticatedGetFetch,
  authenticatedPatchFetch,
  authenticatedPostFetch,
} from "utility/authenticated_fetch";
import { captureSentryException } from "utility/capture_sentry_exception";
import { GenericObject } from "utility/misc";

import { DRFPageResponse, SubmitState } from "./lib";

// -----------------------------------------------------------------------------

export enum FindingSeverity {
  EarlyAccessInfo = 0,
  Info = 1,
  Low = 2,
  Medium = 3,
  High = 4,
  Critical = 5,
}

export enum FindingState {
  New = 1,
  Notified = 2,
  Resolved = 3,
  Suppressed = 4,
  Resolving = 5,
  Acknowledged = 6,
  Source_Disconnected = 7,
}

export enum Source {
  AWSScan = 1,
  AWSCloudTrail = 2,
  Box = 3,
  GithubScan = 4,
  GoogleScan = 5,
  Okta = 6,
  M365 = 7,
  GoogleEvent = 8,
  GCPScan = 9,
  Zoom = 10,
  ZoomScan = 11,
  AzureScan = 12,
}

// -----------------------------------------------------------------------------

export const FindingSeverityLabel: { [prop: number]: string } = {
  [FindingSeverity.EarlyAccessInfo]: "Early Access",
  [FindingSeverity.Info]: "Info",
  [FindingSeverity.Low]: "Low",
  [FindingSeverity.Medium]: "Medium",
  [FindingSeverity.High]: "High",
  [FindingSeverity.Critical]: "Critical",
};

export const FindingSeverityLabelToIntMap: { [prop: string]: FindingSeverity } =
  {
    early_access: FindingSeverity.EarlyAccessInfo,
    info: FindingSeverity.Info,
    low: FindingSeverity.Low,
    medium: FindingSeverity.Medium,
    high: FindingSeverity.High,
    critical: FindingSeverity.Critical,
  };

export const FindingSeverityNameToIntMap: { [prop: string]: FindingSeverity } =
  {
    "Early Access": FindingSeverity.EarlyAccessInfo,
    EarlyAccessInfo: FindingSeverity.EarlyAccessInfo,
    Info: FindingSeverity.Info,
    Low: FindingSeverity.Low,
    Medium: FindingSeverity.Medium,
    High: FindingSeverity.High,
    Critical: FindingSeverity.Critical,
  };

export const FindingStateLabel: { [prop: number]: string } = {
  [FindingState.New]: "New",
  [FindingState.Notified]: "Notified",
  [FindingState.Resolved]: "Resolved",
  [FindingState.Suppressed]: "Ignored",
  [FindingState.Resolving]: "Resolving",
  [FindingState.Acknowledged]: "Acknowledged",
  [FindingState.Source_Disconnected]: "Source Disconnected",
};

export const FindingSeverityColor = {
  [FindingSeverity.Critical]: "critical",
  [FindingSeverity.High]: "high",
  [FindingSeverity.Medium]: "medium",
  [FindingSeverity.Low]: "low",
  [FindingSeverity.Info]: "info",
  [FindingSeverity.EarlyAccessInfo]: "early_access",
};

export const SourceLabel: { [prop: number]: string } = {
  [Source.AWSScan]: "Amazon Web Services (Scan)",
  [Source.AWSCloudTrail]: "Amazon Web Services (CloudTrail)",
  [Source.Box]: "Box",
  [Source.GCPScan]: "Google Cloud Platform (Scan)",
  [Source.GithubScan]: "GitHub (Scan)",
  [Source.GoogleScan]: "Google Workspace (Scan)",
  [Source.GoogleEvent]: "Google Workspace (Events)",
  [Source.Okta]: "Okta",
  [Source.M365]: "Microsoft 365",
  [Source.Zoom]: "Zoom",
  [Source.ZoomScan]: "Zoom (Scan)",
  [Source.AzureScan]: "Azure (Scan)",
};

export const SourceToColor: { [prop: number]: string } = {
  [Source.AWSScan]: "#FF9900",
  [Source.AWSCloudTrail]: "#fdbe5e",
  [Source.Box]: "#92A7F5",
  [Source.GCPScan]: "#3263b5",
  [Source.GithubScan]: "#6213C8",
  [Source.GoogleScan]: "#4286F5",
  [Source.GoogleEvent]: "#b2ccf8",
  [Source.Okta]: "#1E40BC",
  [Source.M365]: "#00dbbf",
  [Source.Zoom]: "#abcdef",
  [Source.ZoomScan]: "#336699",
  [Source.AzureScan]: "#00e6ff",
};

export const SeverityToColor: { [prop: number]: string } = {
  [FindingSeverity.EarlyAccessInfo]: "#50C6CC",
  [FindingSeverity.Info]: "#808080",
  [FindingSeverity.Low]: "#0072FE",
  [FindingSeverity.Medium]: "#EBB000",
  [FindingSeverity.High]: "#FF7700",
  [FindingSeverity.Critical]: "#DE0000",
};

export const OpenStates = [
  FindingState.New,
  FindingState.Notified,
  FindingState.Acknowledged,
  FindingState.Source_Disconnected,
];

export const IgnorableStates = [
  FindingState.Source_Disconnected,
  FindingState.Suppressed,
  FindingState.New,
  FindingState.Notified,
];

// -----------------------------------------------------------------------------

const initialState: FindingStoreState = {
  findings: [],
  findingTypes: {},
  findingQuery: new URLSearchParams(),
  findingsPage: 1,

  findingsAll: [],

  deeplinkFindings: [],
  updateSeverityInProgress: "",
  setOrgSeverityOverrideInProgress: "",
};

const pageSize = 20;

// -----------------------------------------------------------------------------

export const useFindingStore = createHook(
  createStore({
    initialState,
    actions: {
      loadAll,
      load,

      loadDeeplinkFindings,
      setDeepLinkFindings,
      clearDeeplinkFindings,
      resetDeeplinkFindings,

      updateState,
      updateSeverity,

      setOrgSeverityOverride,

      loadMetrics,
      loadRiskScore,
      loadIAMMetrics,

      bulkUpdateState,
      bulkUpdateSeverity,
    },
  })
);

// -----------------------------------------------------------------------------

function loadAll(): Action<FindingStoreState> {
  return async ({ setState, getState, dispatch }) => {
    setState({ loadAllState: SubmitState.STARTED });

    //
    let page = 1;
    let attemptsLeft = 100;
    let findingNames = new Set<string>();
    const existingNames = getState().findingTypes;
    const findings = [];

    function processFinding(datum: ServerFinding): Finding {
      if (existingNames[datum.name] == null) {
        findingNames = findingNames.add(datum.name);
      }

      //
      return {
        ...datum,
        create_time: new Date(datum.create_time),
        event_time: new Date(datum.event_time),
        resolved_time: datum.resolved_time
          ? new Date(datum.resolved_time)
          : undefined,
      };
    }

    try {
      do {
        const data = await authenticatedGetFetch<{
          results: ServerFinding[];
          next: string | null;
        }>(`api/findings?page=${page}`);

        //
        findings.push(...(data.results || []).map(processFinding));

        setState({
          findingsAllCount: findings.length,
          findingsAll: findings.slice(),
        });
        //
        page += 1;
        attemptsLeft -= 1;

        //
        if (data.next === null) {
          break;
        }
      } while (attemptsLeft > 0);
    } catch (error) {
      captureSentryException(error, "Failed to load all findings");
      setState({
        loadAllState: SubmitState.ERROR,
        findingsAllError: error instanceof Error ? error.message : undefined,
        findingsAllCount: undefined,
        findingsAll: undefined,
      });
    }

    //
    dispatch(populateMissingFindingTypes(findingNames));
    setState({
      loadAllState: SubmitState.SUCCESS,
      findingsAllError: undefined,
      findingsAllCount: findings.length,
      findingsAll: findings,
    });
  };
}

function load(
  page: number,
  query: URLSearchParams,
  reload?: boolean
): Action<FindingStoreState> {
  return async ({ getState, setState, dispatch }) => {
    const queryChanged =
      getState().findingQuery.toString() !== query.toString() &&
      !(
        getState().findingQuery.toString() === "state=1&state=2" &&
        query.toString() === ""
      );
    switch (getState().loadState) {
      case SubmitState.STARTED:
        return;
      case SubmitState.SUCCESS: {
        const currentstate = getState();
        if (!reload && page === currentstate.findingsPage && !queryChanged) {
          return;
        }
      }
    }
    if (getState().loadState === SubmitState.SUCCESS) {
      setState({ loadState: SubmitState.STARTED_AGAIN });
    } else {
      setState({ loadState: SubmitState.STARTED });
    }
    try {
      const newState: Partial<FindingStoreState> = {
        loadState: SubmitState.SUCCESS,
        findingsPage: page,
      };

      // construct query string
      if (!query.has("state")) {
        // insert default state filter if it's not in the query
        query.append("state", FindingState.New.toString());
        query.append("state", FindingState.Notified.toString());
      }
      query.sort();
      if (queryChanged) {
        setState({
          findingQuery: query,
          findings: [],
          findingsCount: undefined,
        });
      }

      const data = await authenticatedGetFetch<DRFPageResponse<ServerFinding>>(
        `api/findings?${query.toString()}`
      );

      let findingNames = new Set<string>();
      const { findings = [], findingTypes: existingNames = {} } = getState();

      //
      const newFindingsUuid = (data.results || []).reduce(
        (obj: GenericObject, finding) => {
          obj[finding.uuid] = true;

          return obj;
        },
        {}
      );

      //
      newState.findings = [
        ...findings.filter(
          // this removes deeplink findings if they were loaded earlier
          (finding: Finding) => !newFindingsUuid[finding.uuid]
        ),

        // filter out repeats
        ...data.results.map((datum: ServerFinding): Finding => {
          if (existingNames[datum.name] == null) {
            findingNames = findingNames.add(datum.name);
          }

          return {
            ...datum,
            create_time: new Date(datum.create_time),
            event_time: new Date(datum.event_time),
            resolved_time: datum.resolved_time
              ? new Date(datum.resolved_time)
              : undefined,
          };
        }),
      ];
      newState.findingsCount = data.count;
      newState.findingsPageCount = Math.ceil(data.count / pageSize);
      dispatch(populateMissingFindingTypes(findingNames));
      setState(newState);
    } catch (error) {
      captureSentryException(error, "Failed to load finding page");
      setState({
        loadState: SubmitState.ERROR,
        findingsError: error instanceof Error ? error.message : undefined,
      });
      throw error;
    }
  };
}

function loadDeeplinkFindings(uuids: string[]): Action<FindingStoreState> {
  return async ({ getState, setState, dispatch }) => {
    try {
      setState({
        loadDeeplinkFindings: SubmitState.STARTED,
        deeplinkFindings: [],
        deeplinkFindingsError: undefined,
      });

      //
      const results: ServerFinding[] = await Promise.all(
        uuids.map(
          async (uuid) => await authenticatedGetFetch(`api/findings/${uuid}`)
        )
      );

      //
      let findingNames = new Set<string>();
      const { findingTypes: existingNames = {} } = getState();

      const deeplinkFindings: Finding[] = [];
      for (let index = 0; index < results.length; index += 1) {
        const data: ServerFinding = results[index];

        if (existingNames[data.name] == null) {
          findingNames = findingNames.add(data.name);
        }

        //
        const finding: Finding = {
          ...data,

          // why isn't the uuid in the result data?
          uuid: uuids[index],

          create_time: new Date(data.create_time),
          event_time: new Date(data.event_time),
          resolved_time: data.resolved_time
            ? new Date(data.resolved_time)
            : undefined,
        };

        deeplinkFindings.push(finding);
      }

      setState({
        loadDeeplinkFindings: SubmitState.SUCCESS,
        deeplinkFindingsError: undefined,
        deeplinkFindings,
      });
      dispatch(populateMissingFindingTypes(findingNames));
    } catch (error: any) {
      captureSentryException(error, "Failed to load deeplink findings");
      setState({
        loadDeeplinkFindings: SubmitState.ERROR,
        deeplinkFindingsError: (error as Error).message,
        deeplinkFindings: [],
      });
    }
  };
}

function setDeepLinkFindings(
  newDeepLinkFindings: Finding[]
): Action<FindingStoreState> {
  return ({ setState }) =>
    setState({
      deeplinkFindings: newDeepLinkFindings,
    });
}

function clearDeeplinkFindings(): Action<FindingStoreState> {
  return async ({ setState }) =>
    setState({
      loadDeeplinkFindings: SubmitState.SUCCESS,
      deeplinkFindingsError: undefined,
      deeplinkFindings: [],
    });
}

function resetDeeplinkFindings(): Action<FindingStoreState> {
  return async ({ setState }) =>
    setState({
      loadDeeplinkFindings: undefined,
      deeplinkFindingsError: undefined,
      loadState: undefined,
      deeplinkFindings: [],
    });
}

function updateState(
  uuid: string,
  state: FindingState,
  updateType: string,
  resultFn: Function
): Action<FindingStoreState> {
  return async ({ getState, setState }) => {
    try {
      if (updateType === "ignore") {
        setState({ updateIgnoreInProgress: uuid });
      }

      if (updateType === "acknowledge") {
        setState({ updateAckInProgress: uuid });
      }

      const data = await authenticatedPatchFetch(`api/findings/${uuid}`, {
        state,
      });

      const { findings, findingsAll, deeplinkFindings } = getState();

      // eslint-disable-next-line no-inner-declarations
      function toUpdatedFinding(finding: Finding): Finding {
        return finding.uuid === uuid ? { ...finding, state } : finding;
      }

      const newFindings = findings.map(toUpdatedFinding);
      const newFindingsAll = findingsAll.map(toUpdatedFinding);
      const newDeeplinkFindings = deeplinkFindings.map(toUpdatedFinding);

      setState({
        findings: newFindings,
        findingsAll: newFindingsAll,
        deeplinkFindings: newDeeplinkFindings,
        updateIgnoreInProgress: initialState.updateIgnoreInProgress,
        updateAckInProgress: initialState.updateAckInProgress,
        loadMetricsState: SubmitState.STARTED_AGAIN,
      });
      loadMetrics();

      resultFn(data);
    } catch (error) {
      setState({
        updateIgnoreInProgress: initialState.updateIgnoreInProgress,
        updateAckInProgress: initialState.updateAckInProgress,
      });
      captureSentryException(error, "Failed to update finding");
      resultFn({ status: undefined });
    }
  };
}

function updateSeverity({
  uuid,
  newSeverity,
  onSuccess,
  onError,
}: {
  uuid: string;
  newSeverity: FindingSeverity;
  onSuccess: Function;
  onError: Function;
}): Action<FindingStoreState> {
  return async ({ getState, setState }) => {
    try {
      setState({ updateSeverityInProgress: uuid });

      //
      await authenticatedPatchFetch(`api/findings/${uuid}`, {
        severity: newSeverity,
      });

      //
      const state = getState();
      const newFindings = state.findings.map((finding) => {
        if (finding.uuid === uuid) {
          finding.severity = newSeverity;
          finding.customer_overridden_severity = newSeverity;
        }
        return finding;
      });
      const newFindingsAll = state.findingsAll.map((finding) => {
        if (finding.uuid === uuid) {
          finding.severity = newSeverity;
          finding.customer_overridden_severity = newSeverity;
        }
        return finding;
      });
      const newDeeplinkFindings = state.deeplinkFindings.map((finding) => {
        if (finding.uuid === uuid) {
          finding.severity = newSeverity;
          finding.customer_overridden_severity = newSeverity;
        }
        return finding;
      });
      setState({
        findings: newFindings,
        findingsAll: newFindingsAll,
        deeplinkFindings: newDeeplinkFindings,
        loadMetricsState: SubmitState.STARTED_AGAIN,
      });
      loadMetrics();

      //
      onSuccess();
      setState({
        updateSeverityInProgress: initialState.updateSeverityInProgress,
      });
    } catch (error) {
      captureSentryException(error, "Failed to update severity");
      setState({
        updateSeverityInProgress: initialState.updateSeverityInProgress,
      });
      onError();
    }
  };
}

// This will update the severity of all findings with the given name to the given severity
function setOrgSeverityOverride({
  updatedFinding,
  newSeverity,
  onSuccess,
  onError,
}: {
  updatedFinding: Finding;
  newSeverity: FindingSeverity;
  onSuccess: Function;
  onError: Function;
}): Action<FindingStoreState> {
  return async ({ getState, setState }) => {
    try {
      setState({ setOrgSeverityOverrideInProgress: updatedFinding.name });

      //
      await authenticatedPostFetch(
        "api/organization/finding_severity_overrides",
        {
          severity_overrides: [
            {
              finding_type: updatedFinding.name,
              severity: FindingSeverityLabel[newSeverity],
            },
          ],
          finding_uuids: [updatedFinding.uuid],
        }
      );

      //
      const state = getState();
      const newFindings = state.findings.map((finding) => {
        if (
          finding.name === updatedFinding.name &&
          (finding.customer_overridden_severity === null ||
            finding.uuid === updatedFinding.uuid)
        ) {
          finding.severity = newSeverity;
        }
        return finding;
      });
      const newFindingsAll = state.findingsAll.map((finding) => {
        if (
          finding.name === updatedFinding.name &&
          (finding.customer_overridden_severity === null ||
            finding.uuid === updatedFinding.uuid)
        ) {
          finding.severity = newSeverity;
        }
        return finding;
      });
      const newDeeplinkFindings = state.deeplinkFindings.map((finding) => {
        if (
          finding.name === updatedFinding.name &&
          (finding.customer_overridden_severity === null ||
            finding.uuid === updatedFinding.uuid)
        ) {
          finding.severity = newSeverity;
        }
        return finding;
      });
      setState({
        findings: newFindings,
        findingsAll: newFindingsAll,
        deeplinkFindings: newDeeplinkFindings,
        loadMetricsState: SubmitState.STARTED_AGAIN,
      });
      loadMetrics();
      onSuccess();

      setState({
        setOrgSeverityOverrideInProgress:
          initialState.setOrgSeverityOverrideInProgress,
      });
    } catch (error) {
      captureSentryException(
        error,
        "Failed to set organization severity override"
      );
      setState({
        setOrgSeverityOverrideInProgress:
          initialState.setOrgSeverityOverrideInProgress,
      });
      onError();
    }
  };
}

function loadMetrics(): Action<FindingStoreState> {
  return async ({ getState, setState }) => {
    if (
      getState().loadMetricsState === SubmitState.STARTED ||
      getState().loadMetricsState === SubmitState.SUCCESS
    ) {
      return;
    }

    //
    try {
      setState({ loadMetricsState: SubmitState.STARTED });

      //
      const metrics = await authenticatedGetFetch("api/dashboard");

      //
      metrics.findings = metrics.findings
        .map((finding: ServerFinding) => {
          return {
            ...finding,

            create_time: new Date(finding.create_time),
            event_time: new Date(finding.event_time),
            resolved_time: finding.resolved_time
              ? new Date(finding.resolved_time)
              : undefined,
          };
        })
        .filter((a: Finding) => {
          if (a.state !== FindingState.Source_Disconnected) {
            return true;
          }
          return false;
        })
        .sort((a: Finding, b: Finding) => {
          // Sort first by severity to group severities together, then by time newest to oldest
          if (a.severity > b.severity) {
            return -1;
          } else if (a.severity < b.severity) {
            return 1;
          }
          if (a.event_time >= b.event_time) {
            return -1;
          } else {
            return 1;
          }
        });

      //
      setState({
        loadMetricsState: SubmitState.SUCCESS,
        metrics,
      });
    } catch (error) {
      captureSentryException(error, "Failed to load metrics");
      setState({
        loadMetricsState: SubmitState.ERROR,
        metricsError: error instanceof Error ? error.message : undefined,
      });
      throw error;
    }
  };
}

function loadRiskScore(): Action<FindingStoreState> {
  return async ({ getState, setState }) => {
    if (
      getState().loadRiskScore === SubmitState.STARTED ||
      getState().loadRiskScore === SubmitState.SUCCESS
    ) {
      return;
    }

    //
    try {
      setState({ loadRiskScore: SubmitState.STARTED });

      //
      const responsePayload = await authenticatedGetFetch<{
        risk_score: number | undefined;
      }>("api/dashboard/risk_score");

      //
      setState({
        loadRiskScore: SubmitState.SUCCESS,
        riskScore: responsePayload.risk_score,
      });
    } catch (error) {
      captureSentryException(error, "Failed to load risk score");
      setState({
        loadRiskScore: SubmitState.ERROR,
        riskScoreError: error instanceof Error ? error.message : undefined,
      });
      throw error;
    }
  };
}

function loadIAMMetrics(): Action<FindingStoreState> {
  return async ({ getState, setState }) => {
    if (
      getState().loadMetricsState === SubmitState.STARTED ||
      getState().loadMetricsState === SubmitState.SUCCESS
    ) {
      return;
    }

    try {
      setState({ loadMetricsState: SubmitState.STARTED });

      const responsePayload = await authenticatedGetFetch(
        "api/dashboard/unresovled_by_src_sev?tag=iam"
      );

      responsePayload.findings = responsePayload.findings
        .map((finding: ServerFinding) => {
          return {
            ...finding,

            create_time: new Date(finding.create_time),
            event_time: new Date(finding.event_time),
            resolved_time: finding.resolved_time
              ? new Date(finding.resolved_time)
              : undefined,
          };
        })
        .sort((a: Finding, b: Finding) => {
          // Sort first by severity to group severities together, then by time newest to oldest
          if (a.severity > b.severity) {
            return -1;
          } else if (a.severity < b.severity) {
            return 1;
          }
          if (a.event_time >= b.event_time) {
            return -1;
          } else {
            return 1;
          }
        });

      //
      setState({
        loadMetricsState: SubmitState.SUCCESS,
        metrics: responsePayload.metrics,
      });
    } catch (error) {
      captureSentryException(error, "Failed to load IAM metrics");
      setState({
        loadMetricsState: SubmitState.ERROR,
        metricsError: error instanceof Error ? error.message : undefined,
      });
      throw error;
    }
  };
}

function bulkUpdateState({
  uuids,
  stateType,
  onSuccess,
  onError,
}: {
  uuids: string[];
  stateType: "ignore" | "unignore" | "acknowledge";
  onSuccess: Function;
  onError: Function;
}): Action<FindingStoreState> {
  return async ({ getState, setState }) => {
    if (
      getState().bulkActionState === SubmitState.STARTED ||
      getState().bulkActionState === SubmitState.SUCCESS
    ) {
      return;
    }

    //
    try {
      setState({ bulkActionState: SubmitState.STARTED });

      const newState =
        stateType === "ignore"
          ? FindingState.Suppressed
          : stateType === "unignore"
          ? FindingState.Notified
          : FindingState.Acknowledged;
      const payload = { state: newState };

      // so functional the old way is more "functional"
      for (const uuid of uuids) {
        await authenticatedPatchFetch(`api/findings/${uuid}`, payload);
      }

      //
      const state = getState();
      const newFindings = state.findings.map((finding) => {
        if (uuids.includes(finding.uuid)) {
          finding.state = newState;
        }
        return finding;
      });
      const newFindingsAll = state.findingsAll.map((finding) => {
        if (uuids.includes(finding.uuid)) {
          finding.state = newState;
        }
        return finding;
      });
      const newDeeplinkFindings = state.deeplinkFindings.map((finding) => {
        if (uuids.includes(finding.uuid)) {
          finding.state = newState;
        }
        return finding;
      });

      //
      setState({
        findings: newFindings,
        findingsAll: newFindingsAll,
        deeplinkFindings: newDeeplinkFindings,
        bulkActionState: undefined,
      });
      loadMetrics();

      onSuccess();
    } catch (error) {
      captureSentryException(error, "Failed to bulk update finding state");
      onError();
    }
  };
}

function bulkUpdateSeverity({
  uuids,
  newSeverity,
  onSuccess,
  onError,
}: {
  uuids: string[];
  newSeverity: FindingSeverity;
  onSuccess: Function;
  onError: Function;
}): Action<FindingStoreState> {
  return async ({ getState, setState }) => {
    if (
      getState().bulkActionState === SubmitState.STARTED ||
      getState().bulkActionState === SubmitState.SUCCESS
    ) {
      return;
    }
    try {
      setState({ bulkActionState: SubmitState.STARTED });

      const payload = { severity: newSeverity };

      // so functional the old way is more "functional"
      for (const uuid of uuids) {
        await authenticatedPatchFetch(`api/findings/${uuid}`, payload);
      }

      //
      const state = getState();
      const newFindings = state.findings.map((finding) => {
        if (uuids.includes(finding.uuid)) {
          finding.severity = newSeverity;
          finding.customer_overridden_severity = newSeverity;
        }
        return finding;
      });
      const newFindingsAll = state.findingsAll.map((finding) => {
        if (uuids.includes(finding.uuid)) {
          finding.severity = newSeverity;
          finding.customer_overridden_severity = newSeverity;
        }
        return finding;
      });
      const newDeeplinkFindings = state.deeplinkFindings.map((finding) => {
        if (uuids.includes(finding.uuid)) {
          finding.severity = newSeverity;
          finding.customer_overridden_severity = newSeverity;
        }
        return finding;
      });

      //
      setState({
        findings: newFindings,
        findingsAll: newFindingsAll,
        deeplinkFindings: newDeeplinkFindings,
        bulkActionState: undefined,
      });
      loadMetrics();
      onSuccess();
    } catch (error) {
      captureSentryException(error, "Failed to bulk update finding severity");
      onError();
    }
  };
}

// -----------------------------------------------------------------------------

function populateMissingFindingTypes(
  typeNames: Set<string>
): Action<FindingStoreState> {
  return async ({ getState, setState }) => {
    const findingTypes = getState().findingTypes;

    //
    const filteredTypeNames = Array.from(typeNames).filter(
      (name) => findingTypes[name] === undefined
    );
    if (filteredTypeNames.length < 1) {
      return;
    }

    //
    let count = 0;
    let currentPage = new URLSearchParams();
    const pages = [currentPage];
    filteredTypeNames.forEach((name) => {
      if (count >= 25) {
        count = 0;
        currentPage = new URLSearchParams();
        pages.push(currentPage);
      }

      //
      currentPage.append("name", name);
      count += 1;
    });

    try {
      const addToFindingTypes = (result: FindingType) =>
        (findingTypes[result.name] = result);

      for (const page of pages) {
        const data = await authenticatedGetFetch<DRFPageResponse<FindingType>>(
          `api/findings/types?${page.toString()}`
        );
        data.results.forEach(addToFindingTypes);
      }
    } catch (error) {
      captureSentryException(
        error,
        "Failed to populate missing findings types"
      );
    }

    //
    setState({ findingTypes });
  };
}

// -----------------------------------------------------------------------------

export interface FindingStoreState {
  loadState?: SubmitState;
  findings: Finding[];
  findingTypes: { [name: string]: FindingType | undefined };
  findingQuery: URLSearchParams;
  findingsError?: string;
  findingsPage: number;
  findingsCount?: number;
  findingsPageCount?: number;
  updateIgnoreInProgress?: string;
  updateAckInProgress?: string;

  updateSeverityInProgress: string;
  setOrgSeverityOverrideInProgress: string;

  loadAllState?: SubmitState;
  findingsAll: Finding[];
  findingsAllError?: string;
  findingsAllCount?: number;

  loadDeeplinkFindings?: SubmitState;
  deeplinkFindings: Finding[];
  deeplinkFindingsError?: string;

  loadMetricsState?: SubmitState;
  metrics?: Metrics;
  metricsError?: string;

  loadRiskScore?: SubmitState;
  riskScore?: number;
  riskScoreError?: string;

  bulkActionState?: SubmitState;
}

export interface MysteryDetail {
  name: string;
  [x: string]: any;
}

export interface FindingFix {
  name: string;
  description: string;
  undo: string | null;
}

export interface FindingType {
  name: string;
  description: string;
  default_severity: FindingSeverity;
}

export interface FindingDetails {
  actor?: MysteryDetail;
  asset?: MysteryDetail;
}

export interface FindingBase {
  uuid: string;
  source_id: string;
  source: Source;
  name: string;
  severity: FindingSeverity;
  customer_overridden_severity: FindingSeverity;
  state: FindingState;
  actor: string;
  asset: string;
  details: FindingDetails;
  fix?: FindingFix;
  fix_undo: boolean;
  ignore_reason?: string;
}

export interface Finding extends FindingBase {
  create_time: Date;
  event_time: Date;
  resolved_time?: Date;
}

export interface SeverityCount {
  count: number;
  severity: FindingSeverity;
}

export interface UnresolvedBySource {
  source: Source;
  count: number;
}

export interface Metrics {
  total: number;
  findings_created_last_30_days: number;
  resolved: number;
  findings: Finding[];
  by_severity_open: SeverityCount[];
  unresolved_by_source: UnresolvedBySource[];
}

interface ServerFinding extends FindingBase {
  create_time: string;
  event_time: string;
  resolved_time?: string;
}
